import { useState, useEffect, useRef } from 'react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'

export default function Gallery() {
  const [images, setImages] = useState<any[]>([])
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  useEffect(() => {
    const loadImages = async () => {
      const imageModules = import.meta.glob('@/assets/gallery/*.png', { eager: true })
      const galleryImages = Object.entries(imageModules).map(([path, module]: [string, any], i) => ({
        id: i,
        src: module.default,
      }))
      setImages(galleryImages)
    }
    loadImages()
  }, [])

  return (
    <section id="gallery" ref={ref} className="relative bg-white dark:bg-neutral-900 py-32 overflow-hidden transition-colors duration-500">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          className="mb-20"
        >
          <span className="text-rose-500 font-mono text-xs uppercase tracking-[0.3em] block mb-6">
            Portfolio
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal-900 dark:text-white leading-tight">
            Selected Works
          </h2>
        </motion.div>

        {/* Asymmetric Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.slice(0, 8).map((image, index) => {
            // Create asymmetric sizing
            const isLarge = index === 0 || index === 5
            const isTall = index === 2 || index === 7

            return (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                onClick={() => setSelectedImage(image.src)}
                className={`
                  relative overflow-hidden cursor-pointer group rounded-lg
                  ${isLarge ? 'md:col-span-2 md:row-span-2' : ''}
                  ${isTall ? 'row-span-2' : ''}
                `}
              >
                <div className={`${isLarge ? 'aspect-square' : isTall ? 'aspect-[2/3]' : 'aspect-square'}`}>
                  <img
                    src={image.src}
                    alt={`Work ${index + 1}`}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                </div>

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

                {/* Number */}
                <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-white text-sm font-mono drop-shadow-md">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* View More */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1 }}
          className="mt-16 text-center"
        >
          <a
            href="/album"
            className="inline-flex items-center gap-4 text-charcoal-600 dark:text-neutral-400 hover:text-rose-500 dark:hover:text-rose-400 transition-colors uppercase text-xs tracking-widest font-medium"
          >
            View Complete Portfolio
            <span>→</span>
          </a>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-50 bg-white/95 dark:bg-black/95 backdrop-blur-xl flex items-center justify-center p-8"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-8 right-8 w-12 h-12 flex items-center justify-center text-charcoal-400 dark:text-neutral-400 hover:text-rose-500 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>

            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={selectedImage}
              alt="Gallery"
              className="max-w-full max-h-[85vh] object-contain shadow-2xl rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
